import { useMemo } from "react";

/* Animated Chinese zodiac badge — an ink "chop"/seal, deliberately distinct
   from the Constellation and from the Mayan carved cartouche. A brushed
   cinnabar seal frame with a slowly rotating outer ink ring and the year
   animal's CJK character glowing at center. Built to double as a home-web
   satellite node (like Stand Star next to Values), so it reads small too.

   Modes:
   - outline: dim, for an un-activated satellite node.
   - default: lit, steady slow rotation + glow pulse. */

const CINNABAR = "#C2625A";
const INK = "#E9E2D6";
const CJK = "'Songti SC','STSong','Noto Serif SC','Songti TC',serif";

// Tapered brush-stroke divider — a hand-drawn ink line, not a 1px hairline.
export function ChineseBrushDivider({ flip = false, width = 220 }) {
  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);
  return (
    <svg viewBox="0 0 300 14" width={width} height="14" style={{ display: "block", margin: "0 auto", opacity: 0.7, transform: flip ? "scaleX(-1)" : "none" }}>
      <defs>
        <filter id={`cbd-${uid}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.014 0.02" numOctaves="2" seed="5" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="3.5" />
        </filter>
      </defs>
      <path d="M6 8 C60 2, 110 12, 150 7 C195 1, 250 11, 294 6" stroke={CINNABAR} strokeWidth="2.4"
        fill="none" strokeLinecap="round" filter={`url(#cbd-${uid})`} opacity="0.85" />
      <circle cx="150" cy="7" r="1.4" fill={CINNABAR} opacity="0.5" />
    </svg>
  );
}

export function ChineseGlyph({ char = "龍", size = 150, outline = false, bare = false }) {
  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);
  const lit = !outline;
  const frameOp = lit ? 1 : 0.45;

  // Bare: just the character (no seal/ring), accent-colored, gentle glow pulse.
  if (bare) {
    return (
      <svg viewBox="0 0 150 150" width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
        <defs>
          <filter id={`cng-b-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.4" result="b">
              <animate attributeName="stdDeviation" values="1;2.4;1" dur="4.5s" repeatCount="indefinite" />
            </feGaussianBlur>
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <text x="75" y="84" textAnchor="middle" dominantBaseline="central" fontSize="128"
          fill={CINNABAR} style={{ fontFamily: CJK }} filter={`url(#cng-b-${uid})`}>{char}</text>
      </svg>
    );
  }

  const css = `
    .cng-spin-${uid}{transform-box:fill-box;transform-origin:center;animation:cng-spin-${uid} 100s linear infinite;}
    @keyframes cng-spin-${uid}{to{transform:rotate(360deg);}}
  `;

  // brushed outer ring built from short dashes
  const ticks = Array.from({ length: 32 }, (_, i) => (i / 32) * Math.PI * 2);

  return (
    <svg viewBox="0 0 150 150" width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
      <defs>
        <filter id={`cng-rough-${uid}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.013 0.02" numOctaves="2" seed="9" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="3" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={`cng-glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2" result="b">
            {lit && <animate attributeName="stdDeviation" values="1.5;3.2;1.5" dur="4.5s" repeatCount="indefinite" />}
          </feGaussianBlur>
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <style>{css}</style>

      <g filter={lit ? `url(#cng-glow-${uid})` : undefined}>
        {/* slowly rotating brushed ink ring */}
        <g className={`cng-spin-${uid}`} opacity={lit ? 0.6 : 0.28}>
          {ticks.map((a, i) => {
            const r1 = 64, r2 = i % 4 === 0 ? 71 : 68;
            return <line key={i} x1={75 + Math.cos(a) * r1} y1={75 + Math.sin(a) * r1}
              x2={75 + Math.cos(a) * r2} y2={75 + Math.sin(a) * r2}
              stroke={CINNABAR} strokeWidth={i % 4 === 0 ? 2 : 1} opacity={i % 4 === 0 ? 0.9 : 0.5} />;
          })}
        </g>

        {/* seal "chop" — brushed cinnabar rounded square */}
        <rect x="22" y="22" width="106" height="106" rx="16" fill={`${CINNABAR}14`}
          stroke={CINNABAR} strokeWidth="2.4" opacity={frameOp} filter={`url(#cng-rough-${uid})`} />
        <rect x="29" y="29" width="92" height="92" rx="12" fill="none"
          stroke={CINNABAR} strokeWidth="0.8" opacity={lit ? 0.35 : 0.2} />

        {/* year-animal character */}
        <text x="75" y="79" textAnchor="middle" dominantBaseline="central"
          fontSize="74" fill={lit ? INK : `${INK}88`}
          style={{ fontFamily: CJK }} filter={`url(#cng-rough-${uid})`}>
          {char}
        </text>
      </g>
    </svg>
  );
}
